import React, { useState, useCallback, useContext, createContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

const ConfirmContext = createContext(null);

// Global reference for non-hook usage
let _globalConfirm = null;

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: '', message: '', resolve: null, destructive: false });

  const confirm = useCallback((message, { title = 'Confirm', destructive = false } = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, title, message, resolve, destructive });
    });
  }, []);

  // Set global reference
  _globalConfirm = confirm;

  const handleClose = (result) => {
    state.resolve?.(result);
    setState(prev => ({ ...prev, open: false }));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={() => handleClose(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {state.destructive && <AlertTriangle className="h-5 w-5 text-destructive" />}
              {state.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{state.message}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => handleClose(false)}>Cancel</Button>
            <Button size="sm" variant={state.destructive ? 'destructive' : 'default'} onClick={() => handleClose(true)}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) return (msg) => Promise.resolve(window.confirm(msg));
  return ctx;
}

// Non-hook confirm for use outside components
export function globalConfirm(message, opts = {}) {
  if (_globalConfirm) return _globalConfirm(message, opts);
  return Promise.resolve(window.confirm(message));
}
